const cache = new Map<string, Intl.DateTimeFormat>();
function fmt(key: string, opts: Intl.DateTimeFormatOptions, locale = 'en-GB'): Intl.DateTimeFormat {
  let f = cache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(locale, opts);
    cache.set(key, f);
  }
  return f;
}

export function userTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** Calendar date (YYYY-MM-DD) of an instant in a given IANA zone. */
export function ymdIn(at: string | number, tz: string): string {
  return fmt(`ymd|${tz}`, { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }, 'en-CA').format(new Date(at));
}

export function timeIn(at: string, tz: string): string {
  return fmt(`t|${tz}`, { timeZone: tz, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date(at));
}

const noon = (ymd: string): Date => new Date(`${ymd}T12:00:00Z`);

/** "Sat 7 Nov" */
export function dayLabel(ymd: string): string {
  return fmt('day', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short' }).format(noon(ymd));
}

export function weekdayShort(ymd: string): string {
  return fmt('wd', { timeZone: 'UTC', weekday: 'short' }).format(noon(ymd)).toUpperCase();
}

export function monthYear(ymd: string): string {
  return fmt('my', { timeZone: 'UTC', month: 'long', year: 'numeric' }).format(noon(ymd)).toUpperCase();
}

export function addDays(ymd: string, n: number): string {
  const d = noon(ymd);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export function diffDays(from: string, to: string): number {
  return Math.round((noon(to).getTime() - noon(from).getTime()) / 864e5);
}

export function isoWeek(ymd: string): number {
  const d = noon(ymd);
  const day = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - day + 3);
  const firstThursday = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  return 1 + Math.round(((d.getTime() - firstThursday.getTime()) / 864e5 - 3 + ((firstThursday.getUTCDay() + 6) % 7)) / 7);
}

/** "Now", "5m", "3h", "2d" */
export function ago(iso: string, nowMs: number): string {
  const m = Math.max(0, Math.round((nowMs - Date.parse(iso)) / 6e4));
  if (m < 1) return 'Now';
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.round(h / 24)}d`;
}

/** "2D 04H", or "LIVE" once started. */
export function countdown(iso: string, nowMs: number): string {
  const d = Date.parse(iso) - nowMs;
  if (d <= 0) return 'LIVE';
  return `${Math.floor(d / 864e5)}D ${String(Math.floor((d % 864e5) / 36e5)).padStart(2, '0')}H`;
}

/** Venue time, plus viewer time when the zones differ: "10:00 Tokyo · 01:00 your time". */
export function whenLabel(a: { startsAt: string; timeZone: string; allDay?: boolean; city: string }, viewerTz: string): string {
  if (a.allDay) return 'All day';
  const venue = timeIn(a.startsAt, a.timeZone);
  if (viewerTz === a.timeZone || timeIn(a.startsAt, viewerTz) === venue) return venue;
  const vDay = ymdIn(a.startsAt, viewerTz);
  const eDay = ymdIn(a.startsAt, a.timeZone);
  const shift = vDay === eDay ? '' : vDay > eDay ? ' +1d' : ' −1d';
  return `${venue} ${a.city} · ${timeIn(a.startsAt, viewerTz)}${shift} your time`;
}
